import { useMemo, useState } from "react";
import EquipmentCard from "@/components/EquipmentCard";
import { Button } from "@/components/ui/button";

export default function EquipmentSection({ equipment, categories, loading, onBook }) {
  const [active, setActive] = useState("All");

  const filtered = useMemo(() => {
    if (active === "All") return equipment;
    return equipment.filter((e) => e.category === active);
  }, [active, equipment]);

  return (
    <section
      id="equipment"
      data-testid="equipment-section"
      className="py-16 md:py-24 bg-muted/40 border-b border-border"
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <div className="chip mb-4" data-testid="equipment-eyebrow">Our Machine</div>
            <h2 className="font-heading uppercase text-3xl md:text-5xl font-bold tracking-tight text-secondary leading-tight">
              The right size
              <br /> for tight jobs.
            </h2>
            <p className="mt-3 text-muted-foreground max-w-xl">
              A 1.2T Kubota U10 — compact enough for backyards and side gates,
              capable enough to dig footings, trenches and post holes. Serviced,
              safety-checked and delivered straight to your site.
            </p>
          </div>

          {categories.length > 2 && (
            <div className="flex flex-wrap gap-2" data-testid="category-filter">
              {categories.map((c) => (
                <button
                  key={c}
                  data-testid={`filter-${c.toLowerCase()}`}
                  onClick={() => setActive(c)}
                  className={`px-4 py-2 text-xs uppercase tracking-wider font-heading border transition-colors btn-sharp ${
                    active === c
                      ? "bg-secondary text-white border-secondary"
                      : "bg-white text-secondary border-border hover:border-secondary"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          )}
        </div>

        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="equipment-loading">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-96 bg-white border border-border animate-pulse" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div data-testid="no-equipment" className="text-center py-16 text-muted-foreground">
            No equipment matches this category yet.
          </div>
        ) : (
          <div
            className={`grid gap-6 ${
              filtered.length === 1
                ? "max-w-xl mx-auto"
                : "sm:grid-cols-2 lg:grid-cols-3"
            }`}
            data-testid="equipment-grid"
          >
            {filtered.map((eq) => (
              <EquipmentCard key={eq.id} equipment={eq} onBook={() => onBook(eq)} />
            ))}
          </div>
        )}

        <div className="mt-12 border-t border-border pt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground max-w-md">
            Need something not listed here? We can source extra attachments and larger gear.
            Just ask.
          </p>
          <Button
            data-testid="section-quote-btn"
            onClick={() => onBook(null)}
            className="btn-sharp bg-orange-500 hover:bg-orange-600 text-white font-heading uppercase tracking-wider"
          >
            Request Custom Quote
          </Button>
        </div>
      </div>
    </section>
  );
}
